/**
 * Feature flags (issues #358, #1244): gate unfinished or unreleased features.
 *
 * Model:
 *  - Each flag has a `default` in `src/featureFlagRegistry.ts`: on, off, or
 *    `"dev"` (on in local dev, off in the production build).
 *  - In production the flags are managed in StartupAPI (#1244). The proxy's
 *    config is generated from the registry (`dist/startupapi-features.json`,
 *    synced into drawleather.com): a `default: true` flag is rolled out to
 *    everyone, the rest are off unless an admin enables them for a user or an
 *    account in the StartupAPI admin dashboard (`/users/admin/`).
 *  - The proxy writes what differs from those defaults for this visitor onto
 *    `<html data-startupapi-features>`: `id` turns a flag on, `-id` turns it
 *    off. Everything else is at its registry default. Without the proxy
 *    (local dev, preview deployments) the attribute is absent and every flag
 *    is at its default.
 *  - The e2e build turns every flag on through the test seam below.
 *
 * Reads (`isFeatureEnabled`) are synchronous: the attribute is in the page
 * before any script runs, so gated UI never flickers.
 */
import { hidePreviewFeaturesPref, removePreferenceEntry } from "./preferences";
import { PAID_FEATURES } from "virtual:paid-features";
import { FEATURE_FLAG_DEFS, type FeatureFlagDef } from "./featureFlagRegistry";

export interface FeatureFlag extends Omit<FeatureFlagDef, "default"> {
  /** The default for this build: a `"dev"` default resolves to
   *  `import.meta.env.DEV` (on in local dev, off in production). */
  default: boolean;
}

/** The flag registry, resolved for this build. The data lives in
 *  `src/featureFlagRegistry.ts`, where Node scripts can read it too. */
export const FEATURE_FLAGS: ReadonlyArray<FeatureFlag> = FEATURE_FLAG_DEFS.map(
  (def) => ({
    ...def,
    default: def.default === "dev" ? import.meta.env.DEV : def.default,
  }),
);

/** The visitor's flags from the StartupAPI proxy, read once at load. */
const serverFlags: ReadonlyMap<string, boolean> = readServerFlags();
/** Plaintext overrides from the e2e seam (active only under `__FF_E2E__`). */
const seamOverrides: Record<string, boolean> = readSeamOverrides();

const listeners = new Set<() => void>();

/** The flag's own value: the StartupAPI proxy's verdict for this visitor,
 *  then the e2e seam, then the registry default. The seam comes after the
 *  server so a spec can play the proxy's part; no other e2e run has the
 *  attribute. */
function flagValue(id: string): boolean {
  const server = serverFlags.get(id);
  if (server !== undefined) return server;
  if (Object.prototype.hasOwnProperty.call(seamOverrides, id)) {
    return seamOverrides[id]!;
  }
  const flag = FEATURE_FLAGS.find((f) => f.id === id);
  return flag ? flag.default : false;
}

const PAID = new Set(PAID_FEATURES);

/** Whether flag `id` gates a preview feature: one whose feature doc puts it
 *  on a paid plan (#1204). */
export function isPreviewFlag(id: string): boolean {
  const slugs = FEATURE_FLAGS.find((f) => f.id === id)?.features ?? [];
  return slugs.some((slug) => PAID.has(slug));
}

/** Whether the feature behind flag `id` is on: the flag is on, and, for a
 *  preview feature, the user hasn't opted out of preview features (#1204). */
export function isFeatureEnabled(id: string): boolean {
  if (!flagValue(id)) return false;
  return !(isPreviewFlag(id) && hidePreviewFeaturesPref.get());
}

/** Run `fn` whenever what `isFeatureEnabled` returns may have changed. The
 *  flags themselves are fixed for the page's life (signing in or out and
 *  switching accounts reload it); the preview opt-out changes live. */
export function onFeatureFlagsChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function notify(): void {
  for (const fn of listeners) fn();
}

// The preview opt-out changes what `isFeatureEnabled` returns, so everything
// that follows the flags follows it too, live (#1204).
hidePreviewFeaturesPref.subscribe(() => notify());

// The password-locked developer overrides lived here until #1244; drop any
// encrypted blob a developer's browser still holds.
removePreferenceEntry("leather-art:features");

/**
 * The flags the StartupAPI proxy wrote for this visitor (#1244):
 * `<html data-startupapi-features="hardware-tool -vector-export">`, the delta
 * from each flag's production default. Empty when there's no proxy.
 */
function readServerFlags(): Map<string, boolean> {
  const out = new Map<string, boolean>();
  try {
    const attr = document.documentElement.getAttribute(
      "data-startupapi-features",
    );
    for (const token of (attr ?? "").split(/\s+/)) {
      if (!token) continue;
      if (token.startsWith("-")) out.set(token.slice(1), false);
      else out.set(token, true);
    }
  } catch {
    /* no document (Node scripts) */
  }
  return out;
}

// ---------------------------------------------------------------------
// E2E seam (compiled out of production — `__FF_E2E__` is false there)
// ---------------------------------------------------------------------

/** Whether the e2e test seam is active. `__FF_E2E__` is injected by
 *  vite.config.ts only for the dev-server (`serve`) command in CI — it
 *  bakes `false` into every production build. The `typeof` guard keeps
 *  this safe under vitest, where the define isn't applied. */
function seamActive(): boolean {
  return typeof __FF_E2E__ !== "undefined" && __FF_E2E__ === true;
}

/**
 * Test-only plaintext overrides, read once at module load when the e2e
 * seam is active (absent from the deployed production build, so beta
 * users have no plaintext enable path).
 *
 * Under the seam, every flag DEFAULTS ON so the existing e2e suite sees
 * the same world it did before flags existed — no per-spec changes. A
 * spec opts specific flags off (or back on) via, in priority order:
 *   - `window.__ffTest` — a `Record<string, boolean>` injected with
 *     Playwright `addInitScript` before the app boots (per-flag).
 *   - `?ff=a,b` query param — REPLACES the default with exactly the
 *     listed flags on (`?ff=` → all off, `?ff=all` → all on).
 */
function readSeamOverrides(): Record<string, boolean> {
  if (!seamActive()) return {};
  const out: Record<string, boolean> = {};
  for (const f of FEATURE_FLAGS) out[f.id] = true;
  try {
    const params = new URLSearchParams(window.location.search);
    const ff = params.get("ff");
    if (ff !== null) {
      for (const f of FEATURE_FLAGS) out[f.id] = false;
      if (ff === "all" || ff === "*") {
        for (const f of FEATURE_FLAGS) out[f.id] = true;
      } else {
        for (const id of ff.split(",")) if (id.trim()) out[id.trim()] = true;
      }
    }
    const injected = (window as unknown as { __ffTest?: unknown }).__ffTest;
    if (injected && typeof injected === "object") {
      for (const [k, v] of Object.entries(
        injected as Record<string, unknown>,
      )) {
        out[k] = !!v;
      }
    }
  } catch {
    /* ignore malformed seam input */
  }
  return out;
}
